import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Checkbox,
  FormGroup,
  Chip,
  Typography,
  Box,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";

// Array com os dias da semana
const daysOfWeeksArray = [
  { value: 0, label: "Domingo" },
  { value: 1, label: "Segunda" },
  { value: 2, label: "Terça" },
  { value: 3, label: "Quarta" },
  { value: 4, label: "Quinta" },
  { value: 5, label: "Sexta" },
  { value: 6, label: "Sábado" },
];

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    width: "100%",
  },
  sectionContainer: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(0, 0, 0, 0.02)",
    padding: "16px",
    borderRadius: "12px",
    marginBottom: "16px",
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginTop: "8px",
  },
  timeContainer: {
    display: "flex",
    gap: "16px",
    marginTop: "8px",
  },
}));

export default function FlowBuilderAddScheduleModal({
  open,
  onSave,
  onUpdate,
  data,
  close,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [scheduleType, setScheduleType] = useState("days");
  const [days, setDays] = useState(1);
  const [daysOfWeek, setDaysOfWeek] = useState([]);
  const [hours, setHours] = useState("12");
  const [minutes, setMinutes] = useState("00");

  const [labels, setLabels] = useState({
    title: "Adicionar agendamento ao fluxo",
    btn: "Adicionar",
  });

  useEffect(() => {
    if (open === "edit") {
      if (!data) {
        console.error("Cannot open in edit mode schedule node without data");
        return;
      }

      setLabels({
        title: "Editar agendamento",
        btn: "Salvar",
      });

      if (data.data?.days !== undefined) {
        setScheduleType("days");
        setDays(data.data.days);
      } else {
        setScheduleType("weekdays");
        setDaysOfWeek(data.data?.daysOfWeek || []);
      }

      if (data.data?.scheduleAt) {
        const [h, m] = data.data.scheduleAt.split(":");
        setHours(h || "12");
        setMinutes(m || "00");
      } else {
        setHours("12");
        setMinutes("00");
      }

      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: "Adicionar agendamento ao fluxo",
        btn: "Adicionar",
      });

      setScheduleType("days");
      setDays(1);
      setDaysOfWeek([]);
      setHours("12");
      setMinutes("00");
      setActiveModal(true);
    } else {
      setActiveModal(false);
    }
  }, [open, data]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSave = async () => {
    const formattedHours = hours.padStart(2, "0");
    const formattedMinutes = minutes.padStart(2, "0");
    const formattedTime = `${formattedHours}:${formattedMinutes}`;

    if (open === "edit") {
      if (!data) {
        console.error("Cannot update schedule node without data");
        return;
      }

      handleClose();

      onUpdate({
        ...data,
        data: {
          ...data.data,
          daysOfWeek: scheduleType === "weekdays" ? daysOfWeek : [],
          days: scheduleType === "days" ? days : undefined,
          scheduleAt: formattedTime,
        },
      });
    } else if (open === "create") {
      handleClose();
      onSave({
        daysOfWeek: scheduleType === "weekdays" ? daysOfWeek : [],
        days: scheduleType === "days" ? days : undefined,
        scheduleAt: formattedTime,
      });
    }
  };

  const handleDayToggle = (dayValue) => {
    if (daysOfWeek.includes(dayValue)) {
      setDaysOfWeek(daysOfWeek.filter((day) => day !== dayValue));
    } else {
      setDaysOfWeek([...daysOfWeek, dayValue]);
    }
  };

  const handleScheduleTypeChange = (event) => {
    setScheduleType(event.target.value);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={Boolean(activeModal)}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          style={{
            backgroundColor:
              theme.palette.type === "dark" ? "#2c2c2c" : "#f5f5f5",
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: "16px 24px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {labels.title}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <Close fontSize="small" />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent dividers style={{ padding: "24px" }}>
          <div className={classes.sectionContainer}>
            <FormControl component="fieldset" className={classes.formControl}>
              <FormLabel component="legend">Tipo de agendamento</FormLabel>
              <RadioGroup
                aria-label="schedule-type"
                name="schedule-type"
                value={scheduleType}
                onChange={handleScheduleTypeChange}
              >
                <FormControlLabel
                  value="days"
                  control={<Radio color="primary" />}
                  label="Após X dias"
                />
                <FormControlLabel
                  value="weekdays"
                  control={<Radio color="primary" />}
                  label="Em dias específicos da semana"
                />
              </RadioGroup>
            </FormControl>

            {scheduleType === "days" && (
              <FormControl className={classes.formControl}>
                <TextField
                  label="Número de dias"
                  type="number"
                  value={days}
                  onChange={(e) => setDays(parseInt(e.target.value) || 1)}
                  InputProps={{
                    inputProps: { min: 1 },
                  }}
                  fullWidth
                  variant="outlined"
                  margin="normal"
                  helperText="O fluxo irá aguardar X dias antes de continuar"
                />
              </FormControl>
            )}

            {scheduleType === "weekdays" && (
              <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel component="legend" style={{ marginBottom: "8px" }}>
                  Dias da semana
                </FormLabel>
                <FormGroup row>
                  {daysOfWeeksArray.map((day) => (
                    <FormControlLabel
                      key={day.value}
                      control={
                        <Checkbox
                          checked={daysOfWeek.includes(day.value)}
                          onChange={() => handleDayToggle(day.value)}
                          color="primary"
                        />
                      }
                      label={day.label}
                    />
                  ))}
                </FormGroup>
                <Box className={classes.chipContainer}>
                  {daysOfWeek.map((dayValue) => (
                    <Chip
                      key={dayValue}
                      label={
                        daysOfWeeksArray.find((day) => day.value === dayValue)
                          ?.label
                      }
                      onDelete={() => handleDayToggle(dayValue)}
                      color="primary"
                      size="small"
                    />
                  ))}
                </Box>
              </FormControl>
            )}

            <FormControl className={classes.formControl}>
              <FormLabel component="legend" style={{ marginBottom: "8px" }}>
                Horário específico (opcional)
              </FormLabel>
              <Box className={classes.timeContainer}>
                <TextField
                  label="Horas"
                  type="number"
                  value={hours}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 23) {
                      setHours(value.toString());
                    }
                  }}
                  InputProps={{
                    inputProps: { min: 0, max: 23 },
                  }}
                  variant="outlined"
                  style={{ width: "100px" }}
                />
                <Typography
                  variant="h5"
                  style={{ alignSelf: "center", margin: "0 8px" }}
                >
                  :
                </Typography>
                <TextField
                  label="Minutos"
                  type="number"
                  value={minutes}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0 && value <= 59) {
                      setMinutes(value.toString());
                    }
                  }}
                  InputProps={{
                    inputProps: { min: 0, max: 59 },
                  }}
                  variant="outlined"
                  style={{ width: "100px" }}
                />
              </Box>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ marginTop: "4px" }}
              >
                Formato: 24 horas (ex: 14:30)
              </Typography>
            </FormControl>
          </div>
        </DialogContent>

        <DialogActions style={{ padding: "16px 24px" }}>
          <Button onClick={handleClose} style={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={scheduleType === "weekdays" && daysOfWeek.length === 0}
            style={{ textTransform: "none" }}
          >
            {labels.btn}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
